import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  useTheme,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchInvoicesReport } from "../services/Bills/fetchInvoicesReport";
import { fetchCustomers } from "../services/Customers/CustomersData";

const Invoices = () => {
  const theme = useTheme();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("desc");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [customers, setCustomers] = useState([]);
  const [orderTypeFilter, setOrderTypeFilter] = useState("");
  const [dateSortBy, setDateSortBy] = useState("desc");

  const handleInvoiceClick = (invoice) => {
    setSelectedInvoice(invoice);
    setDialogOpen(true);
  };

  useEffect(() => {
    const loadInvoices = async () => {
      try {
        const data = await fetchInvoicesReport();
        setInvoices(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadInvoices();
  }, []);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await fetchCustomers();
        setCustomers(data || []);
      } catch {
        setCustomers([]);
      }
    };
    loadCustomers();
  }, []);

  useEffect(() => {
    let result = [...invoices];

    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter(inv => {
        // البحث في البيانات الأساسية
        const basicSearch = 
          inv.id?.toString().includes(search) ||
          inv.order_id?.toString().includes(search) ||
          inv.root_id?.toString().includes(search) ||
          inv.customer_id?.toString().includes(search) ||
          inv.date?.includes(search) ||
          inv.total_amount?.toString().includes(search) ||
          inv.sub_total?.toString().includes(search);

        // البحث في اسم العميل
        const customerName = getCustomerName(inv.customer_id);
        const customerSearch = customerName?.toLowerCase().includes(searchLower);

        // البحث في اسم المستخدم
        const userSearch = inv.user?.name?.toLowerCase().includes(searchLower);

        // البحث في طرق الدفع
        const paymentSearch = inv.payments?.some(p => 
          p.payment_method?.toLowerCase().includes(searchLower) ||
          p.amount?.toString().includes(search)
        );

        // البحث في تفاصيل الطلب
        const orderSearch = 
          inv.order?.order_type?.toLowerCase().includes(searchLower) ||
          inv.order?.status?.toLowerCase().includes(searchLower) ||
          inv.order?.table_id?.toString().includes(search);

        // البحث في المنتجات
        const productSearch = inv.order?.items?.some(item =>
          item.product?.name?.toLowerCase().includes(searchLower) ||
          item.product?.barcode?.toLowerCase().includes(searchLower) ||
          item.quantity?.toString().includes(search) ||
          item.price?.toString().includes(search) ||
          item.note?.toLowerCase().includes(searchLower)
        );

        // البحث في معاملات الصندوق
        const transactionSearch = inv.cash_transactions?.some(trans =>
          trans.type?.toLowerCase().includes(searchLower) ||
          trans.amount?.toString().includes(search) ||
          trans.daliy_session_id?.toString().includes(search)
        );

        return basicSearch || customerSearch || userSearch || paymentSearch || 
               orderSearch || productSearch || transactionSearch;
      });
    }

    if (dateFilter.trim()) {
      result = result.filter(inv => inv.date.includes(dateFilter));
    }

    if (paymentFilter.trim()) {
      result = result.filter(inv =>
        inv.payments?.some(p => p.payment_method === paymentFilter)
      );
    }

    // فلترة حسب نوع الطلب
    if (orderTypeFilter.trim()) {
      result = result.filter(inv => inv.order?.order_type === orderTypeFilter);
    }

    // ترتيب حسب القيمة
    if (sortBy === "asc") {
      result.sort((a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount));
    } else {
      result.sort((a, b) => parseFloat(b.total_amount) - parseFloat(a.total_amount));
    }

    // ترتيب حسب التاريخ (بعد الترتيب حسب القيمة)
    if (dateSortBy === "asc") {
      result.sort((a, b) => new Date(a.date) - new Date(b.date));
    } else {
      result.sort((a, b) => new Date(b.date) - new Date(a.date));
    }

    setFilteredInvoices(result);
  }, [search, sortBy, invoices, paymentFilter, dateFilter, customers, orderTypeFilter, dateSortBy]);

  const getCustomerName = (customer_id) => {
    const customer = customers.find(c => String(c.id) === String(customer_id));
    return customer ? customer.name : customer_id;
  };

  // استخراج أنواع الطلبات الفريدة من البيانات
  const uniqueOrderTypes = [...new Set(invoices.map(inv => inv.order?.order_type).filter(Boolean))];

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">❌ {error}</Typography>;

  return (
    <Box sx={{ padding: 3, bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="البحث الشامل (رقم، عميل، منتج، طريقة دفع...)"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 300 } }}
          placeholder="ابحث في أي بيانات..."
        />
        
        <TextField
          label="ترتيب حسب التاريخ"
          select
          size="small"
          value={dateSortBy}
          onChange={(e) => setDateSortBy(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="desc">الأحدث أولاً</MenuItem>
          <MenuItem value="asc">الأقدم أولاً</MenuItem>
        </TextField>

        <TextField
          label="ترتيب حسب القيمة"
          select
          size="small"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="desc">الأعلى أولاً</MenuItem>
          <MenuItem value="asc">الأقل أولاً</MenuItem>
        </TextField>

        <TextField
          label="نوع الطلب"
          select
          size="small"
          value={orderTypeFilter}
          onChange={(e) => setOrderTypeFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">الكل</MenuItem>
          {uniqueOrderTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="فلترة حسب التاريخ"
          type="date"
          size="small"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />

        <TextField
          label="طريقة الدفع"
          select
          size="small"
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">الكل</MenuItem>
          <MenuItem value="cash">كاش</MenuItem>
          <MenuItem value="visa">فيزا</MenuItem>
          <MenuItem value="wallet">محفظة</MenuItem>
        </TextField>
      </Box>

      {/* عرض الفلاتر النشطة */}
      {(orderTypeFilter || dateFilter || paymentFilter || search) && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            الفلاتر النشطة:
          </Typography>
          {orderTypeFilter && (
            <Chip
              label={`نوع الطلب: ${orderTypeFilter}`}
              onDelete={() => setOrderTypeFilter("")}
              size="small"
              color="primary"
            />
          )}
          {dateFilter && (
            <Chip
              label={`التاريخ: ${dateFilter}`}
              onDelete={() => setDateFilter("")}
              size="small"
              color="primary"
            />
          )}
          {paymentFilter && (
            <Chip
              label={`الدفع: ${paymentFilter}`}
              onDelete={() => setPaymentFilter("")}
              size="small"
              color="primary"
            />
          )}
          {search && (
            <Chip
              label={`بحث: ${search}`}
              onDelete={() => setSearch("")}
              size="small"
              color="primary"
            />
          )}
        </Box>
      )}

      {filteredInvoices.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          {search || dateFilter || paymentFilter || orderTypeFilter
            ? "لا توجد فواتير تطابق معايير البحث."
            : "لا توجد فواتير في الفرع هذا حتى الآن."}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            عدد الفواتير: <strong>{filteredInvoices.length}</strong>
          </Typography>
          <Grid container spacing={2}>
            {filteredInvoices.map((invoice) => (
              <Grid item xs={12} sm={6} md={4} key={invoice.id}>
                <Paper
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    transition: "0.3s",
                    "&:hover": {
                      boxShadow: 6,
                      transform: "scale(1.02)",
                      border: `1px solid ${theme.palette.primary.main}`
                    }
                  }}
                  onClick={() => handleInvoiceClick(invoice)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
<Typography variant="subtitle1" fontWeight="bold">
  فاتورة #{invoice.order?.general_increment ?? invoice.id}
</Typography>

                    {invoice.order?.order_type && (
                      <Chip 
                        label={invoice.order.order_type} 
                        size="small" 
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    👤 العميل: <strong>{getCustomerName(invoice.customer_id)}</strong>
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    🛒 الطلب: #{invoice.order_id} — 📅 {invoice.date}
                  </Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2">
                      💰 الإجمالي: <strong style={{ color: theme.palette.success.main }}>
                        {invoice.total_amount} جنيه
                      </strong>
                    </Typography>
                    {parseFloat(invoice.discount) > 0 && (
                      <Typography variant="body2" color="error">
                        🎁 الخصم: {invoice.discount} جنيه
                      </Typography>
                    )}
                    {parseFloat(invoice.service) > 0 && (
                      <Typography variant="body2">
                        🍽️ الخدمة: {invoice.service} جنيه
                      </Typography>
                    )}
                    {parseFloat(invoice.tax) > 0 && (
                      <Typography variant="body2">
                        📋 الضريبة: {invoice.tax} جنيه
                      </Typography>
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    💳 طرق الدفع:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {invoice.payments?.map((pay) => (
                      <Chip
                        key={pay.id}
                        label={`${pay.payment_method.toUpperCase()} — ${pay.amount} ج`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                  
                  {invoice.order?.is_client_order === 1 && (
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label="🌐 طلب عميل" 
                        size="small" 
                        color="secondary"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            bgcolor: theme.palette.background.default
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: theme.palette.primary.main, 
          color: '#fff',
          fontWeight: 'bold'
        }}>
          تفاصيل الفاتورة #{selectedInvoice?.id}
        </DialogTitle>
        <DialogContent sx={{ 
          bgcolor: theme.palette.background.default, 
          color: theme.palette.text.primary,
          mt: 2 
        }}>
          {selectedInvoice && (
            <Box>
              {/* معلومات الفاتورة الأساسية */}
              <Card sx={{ mb: 2, bgcolor: theme.palette.background.paper }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    📋 معلومات الفاتورة
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">رقم الفاتورة</Typography>
                      <Typography variant="body1" fontWeight="bold">#{selectedInvoice.id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">رقم الطلب</Typography>
                      <Typography variant="body1" fontWeight="bold">#{selectedInvoice.order_id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">رقم الفرع</Typography>
                      <Typography variant="body1" fontWeight="bold">#{selectedInvoice.root_id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">العميل</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {getCustomerName(selectedInvoice.customer_id)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">التاريخ</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedInvoice.date}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">المستخدم</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedInvoice.user?.name || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* المبالغ المالية */}
              <Card sx={{ mb: 2, bgcolor: theme.palette.background.paper }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    💰 التفاصيل المالية
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>المجموع الفرعي</strong></TableCell>
                          <TableCell align="right"><strong>{selectedInvoice.sub_total} جنيه</strong></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>الخصم ({selectedInvoice.discount_ratio}%)</TableCell>
                          <TableCell align="right">{selectedInvoice.discount} جنيه</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>الخدمة ({selectedInvoice.service_ratio}%)</TableCell>
                          <TableCell align="right">{selectedInvoice.service} جنيه</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>خدمة التوصيل ({selectedInvoice.delivery_service_ratio}%)</TableCell>
                          <TableCell align="right">{selectedInvoice.delivery_service} جنيه</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>الضريبة ({selectedInvoice.tax_ratio}%)</TableCell>
                          <TableCell align="right">{selectedInvoice.tax} جنيه</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1a472a' : '#e8f5e9' }}>
                          <TableCell><strong>الإجمالي النهائي</strong></TableCell>
                          <TableCell align="right">
                            <strong style={{ fontSize: '1.2rem', color: theme.palette.success.main }}>
                              {selectedInvoice.total_amount} جنيه
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* طرق الدفع */}
              <Card sx={{ mb: 2, bgcolor: theme.palette.background.paper }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    💳 طرق الدفع
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedInvoice.payments?.map((pay) => (
                      <Chip
                        key={pay.id}
                        label={`${pay.payment_method.toUpperCase()} — ${pay.amount} جنيه`}
                        color="success"
                        sx={{ fontWeight: 'bold' }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>

              {/* معلومات الطلب */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" color="primary">
                    🛍️ تفاصيل الطلب
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">نوع الطلب</Typography>
                      <Chip 
                        label={selectedInvoice.order?.order_type || 'N/A'} 
                        size="small" 
                        color="info"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">حالة الطلب</Typography>
                      <Chip 
                        label={selectedInvoice.order?.status || 'N/A'} 
                        size="small" 
                        color={selectedInvoice.order?.status === 'finished' ? 'success' : 'warning'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">وقت الفتح</Typography>
                      <Typography variant="body1">{selectedInvoice.order?.open_time || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">رقم الطاولة</Typography>
                      <Typography variant="body1">#{selectedInvoice.order?.table_id || 'N/A'}</Typography>
                    </Grid>
                    {selectedInvoice.order?.is_client_order === 1 && (
                      <Grid item xs={12}>
                        <Chip 
                          label="🌐 طلب من العميل (Client Order)" 
                          color="secondary" 
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {/* عناصر الطلب */}
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                    المنتجات المطلوبة:
                  </Typography>
                  <TableContainer component={Paper} sx={{ bgcolor: theme.palette.background.paper }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f5' }}>
                          <TableCell><strong>المنتج</strong></TableCell>
                          <TableCell align="center"><strong>الكمية</strong></TableCell>
                          <TableCell align="center"><strong>السعر</strong></TableCell>
                          <TableCell align="center"><strong>الإجمالي</strong></TableCell>
                          <TableCell align="center"><strong>الحالة</strong></TableCell>
                          <TableCell><strong>ملاحظات</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedInvoice.order?.items?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {item.product?.image && (
                                  <Box
                                    component="img"
                                    src={`https://${window.location.hostname.split(".")[0]}.restocash.app/${item.product.image}`}
                                    alt={item.product?.name}
                                    sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                                  />
                                )}
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {item.product?.name || 'N/A'}
                                  </Typography>
                                  {item.product?.barcode && (
                                    <Typography variant="caption" color="text.secondary">
                                      {item.product.barcode}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">{item.quantity}</TableCell>
                            <TableCell align="center">{item.price} جنيه</TableCell>
                            <TableCell align="center"><strong>{item.total} جنيه</strong></TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={item.status} 
                                size="small"
                                color={item.status === 'finished' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{item.note || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {/* معاملات الصندوق */}
              {selectedInvoice.cash_transactions && selectedInvoice.cash_transactions.length > 0 && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" color="primary">
                      🏦 معاملات الصندوق
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {selectedInvoice.cash_transactions.map((transaction) => (
                      <Card key={transaction.id} sx={{ mb: 2, bgcolor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f9f9f9' }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">المبلغ</Typography>
                              <Typography variant="body1" fontWeight="bold" color="success.main">
                                {transaction.amount} جنيه
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">النوع</Typography>
                              <Chip label={transaction.type} size="small" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">التاريخ</Typography>
                              <Typography variant="body1">{transaction.transaction_date}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">الجلسة اليومية</Typography>
                              <Typography variant="body1">#{transaction.daliy_session_id}</Typography>
                            </Grid>
                            {transaction.daily_session && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  بدأت في: {transaction.daily_session.started_at} | 
                                  تاريخ الجلسة: {transaction.daily_session.session_date}
                                </Typography>
                              </Grid>
                            )}
                            {transaction.shifts && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  الشيفت: {transaction.shifts.name || `#${transaction.shifts.id}`} | 
                                  بدأ في: {transaction.shifts.started_at}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* معلومات الكابتن والتوصيل */}
              {(selectedInvoice.order?.captain_order || selectedInvoice.order?.delivery) && (
                <Card sx={{ mb: 2, bgcolor: theme.palette.background.paper }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      🚗 معلومات التوصيل
                    </Typography>
                    {selectedInvoice.order?.captain_order && (
                      <Typography variant="body2">
                        كابتن الطلب: {selectedInvoice.order.captain_order.name || 'N/A'}
                      </Typography>
                    )}
                    {selectedInvoice.order?.delivery && (
                      <Typography variant="body2">
                        مسؤول التوصيل: {selectedInvoice.order.delivery.name || 'N/A'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Invoices;
